/**
 * @file Builds the summary row renderer showing the transaction table's total amount.
 */
import { Statistic, Table, Typography } from "antd";

import {
  getFormattedAmountValue,
  getFormattedDecimalValue,
} from "@components/Amount";

import type { EditableTransaction } from "./types";

const { Text } = Typography;
const { Summary: { Row, Cell } } = Table;

// NOTE: the table always carries the empty editable row, so a single
//       transaction needs no total.
const MIN_ROWS_FOR_TOTAL = 2;

const VALUE_STYLE = { fontSize: 14 };

/**
 * Returns the summary-row renderer for the transaction table.
 */
const getTableSummary = () => {
  // NOTE: the parameter is optional because TypeScript infers the summary
  //       prop of the JavaScript Collection component from its empty
  //       default.
  /**
   * Renders the total amount row, or nothing when there is only the empty editable row.
   */
  const render = (rows: readonly EditableTransaction[] = []) => {
    const shouldNotRender = rows.length <= MIN_ROWS_FOR_TOTAL;

    if (shouldNotRender) {
      return undefined;
    }

    const total = rows
      .map(({ amount }) => amount)
      .reduce((sum, amount) => getFormattedDecimalValue(sum, amount, "plus"), 0);

    return (
      <Row>
        <Cell
          align="right"
          index={1}
          colSpan={2}
        >
          <Text>Total:</Text>
        </Cell>

        <Cell
          index={1}
          colSpan={2}
        >
          <Statistic
            value={getFormattedAmountValue(total)}
            prefix="$"
            valueStyle={VALUE_STYLE}
          />
        </Cell>
      </Row>
    );
  };

  return render;
};

export default getTableSummary;
